import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Rocket, Loader2, Copy, Check, Sparkles, RefreshCw, Radio, BookOpen, FileText, Music, Cloud, Wrench, Film, Dice5, Layers, Trash2, Wand2 } from "lucide-react";
import { api } from "@/lib/api";
import AmpliboeEmail from "@/components/AmpliboeEmail";
import AmpliboePrompts from "@/components/AmpliboePrompts";

const TABS = [{ id: "social", label: "Social campaign" }, { id: "email", label: "Email marketing" }, { id: "prompts", label: "Prompt maker" }];

const auth = () => { const t = localStorage.getItem("fliboe_token"); return t ? { Authorization: `Bearer ${t}` } : {}; };

const APP_ICON = { fm: Radio, flip: BookOpen, pulse: FileText, play: Dice5, astro: Sparkles, fixo: Wrench, showo: Film, monsoon: Cloud, fliboe: Layers };

export default function Ampliboe() {
  const [subApps, setSubApps] = useState([]);
  const [languages, setLanguages] = useState([{code:"en",name:"English",native:"English"}]);
  const [subApp, setSubApp] = useState("fliboe");
  const [goal, setGoal] = useState("");
  const [tone, setTone] = useState("warm, punchy, creator-first");
  const [language, setLanguage] = useState("en");
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [history, setHistory] = useState([]);
  const [copied, setCopied] = useState("");
  // Video generation state
  const [videoPrompt, setVideoPrompt] = useState("");
  const [videoSize, setVideoSize] = useState("720x1280");
  const [videoDuration, setVideoDuration] = useState(4);
  const [videoBusy, setVideoBusy] = useState(false);
  const [videos, setVideos] = useState([]);
  const [videoErr, setVideoErr] = useState("");
  const [suggBusy, setSuggBusy] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  // "Cast me" — user uploads a photo once, Claude vision caches a description,
  // and every future video prepends it to the Sora prompt so the character looks like them.
  const [reference, setReference] = useState(null); // { url, description, filename }
  const [refBusy, setRefBusy] = useState(false);
  const [useReference, setUseReference] = useState(false);
  const [tab, setTab] = useState("social");

  const applyVideoPrompt = (p, platform) => {
    setVideoPrompt(p);
    setVideoSize(["youtube", "facebook", "linkedin", "x"].includes(platform) ? "1280x720" : "720x1280");
    setTab("social");
    setTimeout(() => document.querySelector('[data-testid="amp-video-panel"]')?.scrollIntoView({ behavior: "smooth" }), 100);
  };

  useEffect(() => {
    api.get("/ampliboe/config").then(r => { setSubApps(r.data?.sub_apps || []); if (r.data?.languages?.length) setLanguages(r.data.languages); });
    api.get("/ampliboe/drafts", { headers: auth() }).then(r => setHistory(r.data?.drafts || [])).catch(() => {});
    api.get("/ampliboe/videos", { headers: auth() }).then(r => setVideos(r.data?.videos || [])).catch(() => {});
    api.get("/ampliboe/video/reference", { headers: auth() }).then(r => {
      if (r.data?.filename) { setReference(r.data); setUseReference(true); }
    }).catch(() => {});
  }, []);

  const uploadReference = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { setVideoErr("Reference must be an image (JPG / PNG / WebP)."); return; }
    if (file.size > 8 * 1024 * 1024) { setVideoErr("Reference image must be under 8MB."); return; }
    setVideoErr(""); setRefBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/ampliboe/video/reference", fd, { headers: { ...auth(), "Content-Type": "multipart/form-data" }, timeout: 60000 });
      setReference(data);
      setUseReference(true);
    } catch (ex) {
      setVideoErr(ex?.response?.data?.detail || "Could not analyze image.");
    } finally { setRefBusy(false); }
  };

  const removeReference = async () => {
    try { await api.delete("/ampliboe/video/reference", { headers: auth() }); } catch (_) {}
    setReference(null); setUseReference(false);
  };

  const gen = async () => {
    if (goal.trim().length < 5) { setErr("Describe the campaign goal (5+ chars)."); return; }
    setErr(""); setBusy(true); setDraft(null);
    try {
      const { data } = await api.post("/ampliboe/draft", { sub_app: subApp, goal, tone, language, include_hashtags: true }, { headers: auth(), timeout: 90000 });
      setDraft(data);
      setHistory(h => [data, ...h].slice(0, 30));
      // Prefill video prompt from the AI's image_prompt as a nice starting point.
      if (data?.image_prompt && !videoPrompt) setVideoPrompt(data.image_prompt);
    } catch (e) {
      const d = e?.response?.data?.detail;
      setErr(Array.isArray(d) ? d.map(x => x?.msg || JSON.stringify(x)).join(" · ") : (typeof d === "string" ? d : "Draft failed"));
    } finally { setBusy(false); }
  };

  const genVideo = async () => {
    if (videoPrompt.trim().length < 8) { setVideoErr("Describe the video (8+ chars)."); return; }
    setVideoErr(""); setVideoBusy(true);
    try {
      // Kick off the render — backend returns { job_id } immediately.
      const { data: kicked } = await api.post("/ampliboe/video",
        { prompt: videoPrompt, size: videoSize, duration: videoDuration, sub_app: subApp, use_reference: !!(useReference && reference?.description) },
        { headers: auth(), timeout: 30000 });
      const jobId = kicked?.job_id;
      if (!jobId) throw new Error("No job id");
      // Show a placeholder card immediately so the user sees progress.
      setVideos(v => [{ job_id: jobId, status: "queued", size: videoSize, duration: videoDuration, prompt: videoPrompt }, ...v]);
      // Poll every 6 seconds up to ~5 minutes.
      const started = Date.now();
      while (Date.now() - started < 300000) {
        await new Promise(r => setTimeout(r, 6000));
        try {
          const { data } = await api.get(`/ampliboe/video/${jobId}`, { headers: auth(), timeout: 15000 });
          setVideos(v => v.map(x => x.job_id === jobId ? data : x));
          if (data.status === "ready") return;
          if (data.status === "failed") { setVideoErr(data.error || "Render failed"); return; }
        } catch (_) { /* transient — keep polling */ }
      }
      setVideoErr("Still rendering — check again in a minute (video will appear when done).");
    } catch (e) {
      const d = e?.response?.data?.detail;
      setVideoErr(Array.isArray(d) ? d.map(x => x?.msg || JSON.stringify(x)).join(" · ") : (typeof d === "string" ? d : "Video failed"));
    } finally { setVideoBusy(false); }
  };

  const copy = (key, text) => {
    navigator.clipboard.writeText(text);
    setCopied(key); setTimeout(() => setCopied(""), 1500);
  };

  const suggestPrompts = async () => {
    const seed = videoPrompt.trim() || goal.trim();
    if (seed.length < 4) { setVideoErr("Type a short idea first (or fill the campaign goal above)."); return; }
    setVideoErr(""); setSuggBusy(true); setSuggestions([]);
    try {
      const { data } = await api.post("/ampliboe/video/suggestions", { seed, sub_app: subApp }, { headers: auth(), timeout: 90000 });
      setSuggestions(data?.options || []);
    } catch (ex) {
      setVideoErr(ex?.response?.data?.detail || "Could not generate suggestions.");
    } finally { setSuggBusy(false); }
  };

  const deleteVideo = async (jobId) => {
    if (!window.confirm("Delete this video job and its file? Not reversible.")) return;
    try {
      await api.delete(`/ampliboe/video/${jobId}`, { headers: auth() });
      setVideos(vs => vs.filter(v => (v.job_id || v.video_id) !== jobId));
    } catch (ex) {
      alert(ex?.response?.data?.detail || "Delete failed");
    }
  };

  const openIntent = (net, text, url) => {
    const t = encodeURIComponent(text);
    const u = encodeURIComponent(url);
    const map = {
      x: `https://twitter.com/intent/tweet?text=${t}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${u}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${u}&quote=${t}`,
      whatsapp: `https://api.whatsapp.com/send?text=${t}`,
    };
    window.open(map[net], "_blank", "noopener,noreferrer");
  };

  const platforms = draft ? [
    { key: "x",         label: "X / Twitter", text: draft.posts?.x || "" },
    { key: "linkedin",  label: "LinkedIn",    text: draft.posts?.linkedin || "" },
    { key: "facebook",  label: "Facebook",    text: draft.posts?.facebook || "" },
    { key: "instagram", label: "Instagram",   text: draft.posts?.instagram || "" },
    { key: "tiktok",    label: "TikTok caption", text: draft.posts?.tiktok || "" },
    { key: "youtube_short", label: "YouTube Short (60s script)", text: draft.posts?.youtube_short || "" },
    { key: "youtube_long_description", label: "YouTube long-form description", text: draft.posts?.youtube_long_description || "" },
  ] : [];

  return (
    <main className="min-h-screen bg-[#0b0812] text-white" data-testid="ampliboe-page">
      <header className="border-b border-white/10 bg-black/40 backdrop-blur">
        <div className="max-w-6xl mx-auto px-5 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2"><Rocket className="w-5 h-5 text-fuchsia-400" /><span className="fl-display font-black tracking-tight">Ampliboe <span className="text-fuchsia-400 text-[10px] uppercase tracking-widest ml-1">Fliboe marketing</span></span></Link>
          <Link to="/admin" className="fl-btn ghost !py-1.5 !px-3 text-xs">← Admin</Link>
        </div>
      </header>
      <nav className="border-b border-white/10 bg-black/20">
        <div className="max-w-6xl mx-auto px-5 flex gap-1 overflow-x-auto" data-testid="amp-tabs">
          {TABS.map(t => (
            <button key={t.id} type="button" onClick={() => setTab(t.id)}
              className={`px-3 py-2.5 text-xs fl-mono uppercase tracking-widest border-b-2 whitespace-nowrap transition-colors ${tab === t.id ? "border-fuchsia-400 text-white" : "border-transparent text-white/50 hover:text-white/80"}`}
              data-testid={`amp-tab-${t.id}`}>{t.label}</button>
          ))}
        </div>
      </nav>

      {tab === "email" && <AmpliboeEmail subApps={subApps} />}
      {tab === "prompts" && <AmpliboePrompts onUseVideo={applyVideoPrompt} />}

      <section className={`max-w-6xl mx-auto px-5 py-8 grid lg:grid-cols-3 gap-6 ${tab === "social" ? "" : "hidden"}`}>
        <div className="lg:col-span-1">
          <div className="fl-mono text-[11px] uppercase tracking-widest text-fuchsia-400">// Compose</div>
          <h1 className="fl-display text-3xl font-black mt-1">Draft a campaign.</h1>
          <p className="text-slate-400 text-sm mt-2">Pick a sub-app. Tell Ampliboe your goal. It writes X, LinkedIn, Facebook, Instagram, TikTok, and YouTube in one shot.</p>

          <div className="mt-5 space-y-3">
            <label className="block">
              <span className="fl-mono text-[10px] uppercase tracking-widest text-white/60">Sub-app to promote</span>
              <div className="mt-2 grid grid-cols-3 gap-1.5" data-testid="amp-subapps">
                {subApps.map(s => {
                  const Icon = APP_ICON[s.id] || Layers;
                  const active = subApp === s.id;
                  return (
                    <button key={s.id} onClick={() => setSubApp(s.id)}
                      className={`p-2 rounded-lg border text-[11px] transition ${active ? "bg-fuchsia-500/20 border-fuchsia-400 text-white" : "bg-white/[0.02] border-white/10 text-white/70 hover:border-white/25"}`}
                      data-testid={`amp-sub-${s.id}`}>
                      <Icon className="w-3.5 h-3.5 mx-auto mb-1 text-fuchsia-300" />
                      <div className="font-semibold">{s.name.replace("Fliboe ", "")}</div>
                    </button>
                  );
                })}
              </div>
            </label>

            <label className="block">
              <span className="fl-mono text-[10px] uppercase tracking-widest text-white/60">Campaign goal</span>
              <textarea className="fl-input w-full mt-1 min-h-[100px]" value={goal} onChange={e => setGoal(e.target.value)}
                placeholder="Launch Showo Music Video category · target 10k signups in 30 days · Gen-Z creators"
                data-testid="amp-goal" />
            </label>

            <label className="block">
              <span className="fl-mono text-[10px] uppercase tracking-widest text-white/60">Tone</span>
              <input className="fl-input w-full mt-1" value={tone} onChange={e => setTone(e.target.value)} data-testid="amp-tone" />
            </label>

            {err && <div className="text-red-300 text-sm" data-testid="amp-err">{err}</div>}

            <button onClick={gen} disabled={busy} className="fl-btn !bg-fuchsia-500 !text-black w-full justify-center" data-testid="amp-generate">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} {busy ? "Ampliboe is thinking…" : "Draft campaign"}
            </button>
          </div>

          {/* History */}
          {history.length > 0 && (
            <div className="mt-8">
              <div className="fl-mono text-[10px] uppercase tracking-widest text-white/50 mb-2">// Recent drafts</div>
              <div className="space-y-1.5">
                {history.slice(0, 8).map(h => (
                  <button key={h.draft_id} onClick={() => setDraft(h)} className="w-full text-left p-2 rounded bg-white/[0.02] border border-white/10 hover:border-fuchsia-400/40 text-xs" data-testid={`amp-hist-${h.draft_id}`}>
                    <div className="text-fuchsia-300 fl-mono">{h.sub_app_name}</div>
                    <div className="text-white/70 truncate">{h.goal}</div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Output */}
        <div className="lg:col-span-2">
          {!draft ? (
            <div className="fl-card p-8 bg-white/[0.02] border-white/10 text-center min-h-[400px] flex items-center justify-center" data-testid="amp-empty">
              <div>
                <Rocket className="w-10 h-10 text-fuchsia-400/50 mx-auto" />
                <h3 className="fl-display text-xl font-black mt-3">Nothing drafted yet.</h3>
                <p className="text-slate-400 text-sm mt-2 max-w-md mx-auto">Set a goal + sub-app on the left, hit <strong>Draft campaign</strong>, and get 7 platform-ready posts + an image prompt in ~30 seconds.</p>
                <p className="text-slate-500 text-[11px] mt-3">Ampliboe copies drafts to your clipboard — you paste and post. No account bans, no $200/mo X fees, no API keys to manage.</p>
              </div>
            </div>
          ) : (
            <div className="space-y-4" data-testid="amp-output">
              <div className="fl-card p-4 bg-fuchsia-500/10 border-fuchsia-400/30">
                <div className="fl-mono text-[10px] uppercase tracking-widest text-fuchsia-300">// Campaign · {draft.sub_app_name}</div>
                <div className="mt-1 text-sm text-white/90">{draft.goal}</div>
                {draft.headlines?.length > 0 && (
                  <div className="mt-3 grid sm:grid-cols-3 gap-2">
                    {draft.headlines.map((h, i) => (
                      <div key={i} className="p-2 rounded bg-black/40 border border-white/10 text-xs text-white/90" data-testid={`amp-headline-${i}`}>{h}</div>
                    ))}
                  </div>
                )}
              </div>

              {platforms.filter(p => p.text).map(p => (
                <div key={p.key} className="fl-card p-4 bg-white/[0.02] border-white/10" data-testid={`amp-post-${p.key}`}>
                  <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                    <div className="fl-mono text-[11px] uppercase tracking-widest text-fuchsia-300">// {p.label}</div>
                    <div className="flex gap-2">
                      <button onClick={() => copy(p.key, p.text)} className="fl-btn ghost !py-1 !px-2 text-xs" data-testid={`amp-copy-${p.key}`}>
                        {copied === p.key ? <><Check className="w-3 h-3 text-emerald-400" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
                      </button>
                      {p.key === "x" && <button onClick={() => openIntent("x", p.text, draft.url)} className="fl-btn ghost !py-1 !px-2 text-xs">Post to X</button>}
                      {p.key === "linkedin" && <button onClick={() => openIntent("linkedin", p.text, draft.url)} className="fl-btn ghost !py-1 !px-2 text-xs">Post to LinkedIn</button>}
                      {p.key === "facebook" && <button onClick={() => openIntent("facebook", p.text, draft.url)} className="fl-btn ghost !py-1 !px-2 text-xs">Post to FB</button>}
                    </div>
                  </div>
                  <pre className="whitespace-pre-wrap font-sans text-sm text-white/90 leading-relaxed">{p.text}</pre>
                </div>
              ))}

              {draft.image_prompt && (
                <div className="fl-card p-4 bg-white/[0.02] border-white/10" data-testid="amp-image-prompt">
                  <div className="flex items-center justify-between mb-2">
                    <div className="fl-mono text-[11px] uppercase tracking-widest text-fuchsia-300">// Hero image prompt (paste into Nano Banana / Midjourney)</div>
                    <button onClick={() => copy("image", draft.image_prompt)} className="fl-btn ghost !py-1 !px-2 text-xs">{copied === "image" ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}</button>
                  </div>
                  <p className="text-sm text-white/85">{draft.image_prompt}</p>
                </div>
              )}

              {draft.hashtags?.length > 0 && (
                <div className="fl-card p-4 bg-white/[0.02] border-white/10">
                  <div className="fl-mono text-[10px] uppercase tracking-widest text-fuchsia-300 mb-2">// Hashtags</div>
                  <div className="flex flex-wrap gap-1.5">
                    {draft.hashtags.map((h, i) => <span key={i} className="text-xs bg-black/40 border border-white/10 rounded px-2 py-1 text-white/80">{h.startsWith("#") ? h : `#${h}`}</span>)}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Video generator */}
          <div className="fl-card p-4 mt-6 bg-white/[0.02] border-white/10" data-testid="amp-video-panel">
            <div className="fl-mono text-[10px] uppercase tracking-widest text-fuchsia-300 mb-2">// Video generator · Wan 2.5 (fal.ai)</div>
            {/* Cast me — reference-image upload */}
            <div className="mb-3 p-3 rounded-lg border border-white/10 bg-black/30" data-testid="amp-video-ref-panel">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="fl-mono text-[10px] uppercase tracking-widest text-fuchsia-300">// Cast yourself (optional)</div>
                {reference?.description && (
                  <label className="flex items-center gap-2 text-[11px] text-white/70 cursor-pointer" data-testid="amp-video-ref-toggle-label">
                    <input type="checkbox" checked={useReference} onChange={e => setUseReference(e.target.checked)} data-testid="amp-video-ref-toggle" />
                    Use my likeness in generated video
                  </label>
                )}
              </div>
              {reference?.url ? (
                <div className="mt-2 flex items-start gap-3">
                  <img src={reference.url} alt="reference" className="w-16 h-16 rounded object-cover border border-white/10" data-testid="amp-video-ref-thumb" />
                  <div className="flex-1 min-w-0">
                    <div className="text-[11px] text-white/80 line-clamp-3" data-testid="amp-video-ref-desc">{reference.description}</div>
                    <div className="mt-1 flex items-center gap-2">
                      <label className="fl-btn ghost !py-1 !px-2 text-[11px] cursor-pointer" data-testid="amp-video-ref-replace">
                        Replace photo
                        <input type="file" accept="image/*" className="hidden" onChange={e => uploadReference(e.target.files?.[0])} disabled={refBusy} />
                      </label>
                      <button type="button" onClick={removeReference} className="fl-btn ghost !py-1 !px-2 text-[11px] !text-red-300" data-testid="amp-video-ref-remove">Remove</button>
                      {refBusy && <span className="text-[11px] text-fuchsia-300 inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Analyzing…</span>}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="mt-2">
                  <label className="fl-btn ghost !py-1.5 !px-3 text-xs cursor-pointer inline-flex items-center gap-2" data-testid="amp-video-ref-upload">
                    {refBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                    {refBusy ? "Analyzing photo…" : "Upload your photo"}
                    <input type="file" accept="image/*" className="hidden" onChange={e => uploadReference(e.target.files?.[0])} disabled={refBusy} />
                  </label>
                  <div className="text-[10px] text-white/40 mt-1">Photo is described in ~40 words by AI, then prepended to the prompt so the generated character looks like you. Photo never leaves your Fliboe workspace.</div>
                </div>
              )}
            </div>
            <textarea
              className="fl-input w-full min-h-[80px]"
              placeholder="e.g. A vertical 9:16 shot of prayer flags whipping in the Himalayan wind at dusk, snow peaks in the background, warm cinematic grade, no text"
              value={videoPrompt}
              onChange={e => setVideoPrompt(e.target.value)}
              data-testid="amp-video-prompt"
            />
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <button onClick={suggestPrompts} disabled={suggBusy} className="fl-btn ghost !py-1.5 !px-3 text-xs" data-testid="amp-video-suggest">
                {suggBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
                {suggBusy ? "Cooking 5 options…" : "Suggest 5 diverse prompts"}
              </button>
              {suggestions.length > 0 && (
                <button onClick={() => setSuggestions([])} className="text-[11px] text-white/40 hover:text-white/80" data-testid="amp-video-suggest-clear">clear</button>
              )}
            </div>
            {suggestions.length > 0 && (
              <div className="mt-2 grid gap-2" data-testid="amp-video-suggestions">
                {suggestions.map((s, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => { setVideoPrompt(s.prompt); setSuggestions([]); }}
                    className="text-left p-2 rounded-md bg-white/5 hover:bg-fuchsia-500/15 border border-white/10 hover:border-fuchsia-400/40 transition"
                    data-testid={`amp-video-sugg-${i}`}
                  >
                    <div className="fl-mono text-[10px] uppercase tracking-widest text-fuchsia-300">Option {i + 1} · {s.title}</div>
                    <div className="text-white/85 text-xs mt-1 line-clamp-3">{s.prompt}</div>
                  </button>
                ))}
              </div>
            )}
            <div className="mt-2 grid sm:grid-cols-3 gap-2">
              <select className="fl-input" value={videoSize} onChange={e => setVideoSize(e.target.value)} data-testid="amp-video-size">
                <option value="720x1280">Vertical 9:16 (TikTok / Reels)</option>
                <option value="1280x720">Horizontal 16:9 (YouTube)</option>
              </select>
              <select className="fl-input" value={videoDuration} onChange={e => setVideoDuration(Number(e.target.value))} data-testid="amp-video-duration">
                <option value={4}>5 seconds (cheapest)</option>
                <option value={8}>10 seconds</option>
              </select>
              <button onClick={genVideo} disabled={videoBusy} className="fl-btn !bg-fuchsia-500 !text-black justify-center" data-testid="amp-video-run">
                {videoBusy ? <><Loader2 className="w-4 h-4 animate-spin" /> Rendering (~1-3 min)…</> : <><Sparkles className="w-4 h-4" /> Generate video</>}
              </button>
            </div>
            {videoErr && <div className="text-red-300 text-sm mt-2" data-testid="amp-video-err">{videoErr}</div>}

            {videos.length > 0 && (
              <div className="mt-4 grid sm:grid-cols-2 gap-3">
                {videos.map(v => (
                  <div key={v.job_id || v.video_id} className="rounded-lg overflow-hidden border border-white/10 bg-black/40" data-testid={`amp-video-${v.job_id || v.video_id}`}>
                    {v.url ? (
                      <video src={v.url} controls playsInline className="w-full aspect-video bg-black" />
                    ) : v.status === "failed" ? (
                      <div className="w-full aspect-video bg-black grid place-items-center text-xs px-4" data-testid={`amp-video-failed-${v.job_id}`}>
                        <div className="text-center text-red-300">
                          <div className="fl-mono uppercase tracking-widest">✗ Failed</div>
                          <div className="mt-1 text-white/60 line-clamp-3">{v.error || "Render failed — try again."}</div>
                        </div>
                      </div>
                    ) : (
                      <div className="w-full aspect-video bg-black grid place-items-center text-white/60 text-xs fl-mono">
                        <div className="text-center">
                          <Loader2 className="w-6 h-6 animate-spin mx-auto text-fuchsia-400" />
                          <div className="mt-2 uppercase tracking-widest">{v.status || "queued"}…</div>
                          <div className="mt-1 text-white/40">Rendering (~1-3 min)</div>
                        </div>
                      </div>
                    )}
                    <div className="p-2 flex items-center justify-between gap-2">
                      <div className="text-[10px] text-white/50 fl-mono truncate flex-1">{v.duration}s · {v.size} {v.status === "failed" ? "· ✗ failed" : ""}</div>
                      <div className="flex items-center gap-1">
                        {v.url && <a href={v.url} download className="fl-btn ghost !py-1 !px-2 text-xs" data-testid={`amp-video-dl-${v.job_id || v.video_id}`}>Download</a>}
                        <button onClick={() => deleteVideo(v.job_id || v.video_id)} className="fl-btn ghost !py-1 !px-2 text-xs !text-red-300 hover:!bg-red-500/10" data-testid={`amp-video-del-${v.job_id || v.video_id}`} title="Delete video">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
    </main>
  );
}
